import axios, { AxiosError } from 'axios';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import { DefaultGetAllResponse } from '~/App/Api/rest';
import usePagination, { PaginationOptions } from '~/react-hooks/usePagination';
import { Profile, Recording } from '~/types/models';
import { ResourceTag, TestimonialRespondent } from '~/types/models/testimonial';
import { parseRichTextToSlateData, RichText, SlateData } from '@vimily/bonjoro-ui-components-v2/RichTextEditor';

export interface UserAsset {
  uuid: string;
  name: string;
  content: string;
  content_rich: SlateData[];
  created_at: string;
  author: Profile | Partial<TestimonialRespondent> | null;
  tags?: ResourceTag[];
  video?: Recording;
}

export interface UserAssetsFilter {
  page?: number;
  per_page?: number;
}

export const userAssetsQueryKeys = {
  all: ['user-assets'],
  lists: () => [...userAssetsQueryKeys.all, 'list'] as const,
  list: (filters: UserAssetsFilter) =>
    [...userAssetsQueryKeys.lists(), { ...filters }] as const,
};

/**
 * User Asset API actions
 */
export const userAssetsApiUrl = '/api/v3/user-assets';

export type GetUserAssetsParams = UserAssetsFilter;

export const getUserAssets = async (params: GetUserAssetsParams) => {
  const response = await axios.get<DefaultGetAllResponse<UserAsset>>(
    userAssetsApiUrl,
    {
      params,
    }
  );

  return response.data;
};

export interface UserAssetParams {
  name?: string;
  content_rich?: RichText;
  profile_uuid?: string;
  tags?: string[];
}

export interface CreateUserAssetParams extends UserAssetParams {}

export interface CreateUserAssetResponse {
  message: string;
  data: UserAsset;
}

export const createUserAsset = async ({
  name = 'Untitled',
  content_rich,
  ...params
}: CreateUserAssetParams) => {
  const payload = { ...params, name };

  if (content_rich) {
    payload['content_rich'] = JSON.stringify(
      parseRichTextToSlateData(content_rich)
    );
  }

  const response = await axios.post<CreateUserAssetResponse>(
    userAssetsApiUrl,
    payload
  );

  return response.data;
};

export interface UpdateUserAssetParams extends UserAssetParams {
  uuid: string;
  thumbnail?: string;
}

export const updateUserAsset = async ({
  uuid,
  content_rich,
  ...params
}: UpdateUserAssetParams) => {
  const payload = { ...params };

  if (content_rich) {
    payload['content_rich'] = JSON.stringify(
      parseRichTextToSlateData(content_rich)
    );
  }

  const response = await axios.put<UserAsset>(
    `${userAssetsApiUrl}/${uuid}`,
    payload
  );

  return response.data;
};

export interface DeleteUserAssetParams {
  uuid: string;
}

export interface DeleteUserAssetResponse {
  message: string;
}

export const deleteUserAsset = async ({ uuid }: DeleteUserAssetParams) => {
  const response = await axios.delete<DeleteUserAssetResponse>(
    `${userAssetsApiUrl}/${uuid}`
  );

  return response.data;
};

/**
 * User Asset Hooks
 */
export interface UseUserAssetsParams {
  filters: UserAssetsFilter;
  options?: UseQueryOptions<DefaultGetAllResponse<UserAsset>, AxiosError>;
}

export const useUserAssets = (params: UseUserAssetsParams) => {
  return useQuery<DefaultGetAllResponse<UserAsset>, AxiosError>(
    userAssetsQueryKeys.list(params.filters),
    () => getUserAssets(params.filters),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      suspense: false,
      ...params.options,
    }
  );
};

export function useCreateUserAsset(
  options?: UseMutationOptions<
    CreateUserAssetResponse,
    AxiosError,
    CreateUserAssetParams
  >
) {
  const queryClient = useQueryClient();

  return useMutation(createUserAsset, {
    onSuccess: () => {
      void queryClient.invalidateQueries(userAssetsQueryKeys.lists());
    },
    ...options,
  });
}

export function useUpdateUserAsset(
  options?: UseMutationOptions<UserAsset, AxiosError, UpdateUserAssetParams>
) {
  const queryClient = useQueryClient();

  return useMutation(updateUserAsset, {
    onSuccess: () => {
      void queryClient.invalidateQueries(userAssetsQueryKeys.lists());
    },
    ...options,
  });
}

export function useDeleteUserAsset(
  options?: UseMutationOptions<
    DeleteUserAssetResponse,
    AxiosError,
    DeleteUserAssetParams
  >
) {
  const queryClient = useQueryClient();

  return useMutation(deleteUserAsset, {
    onSuccess: () => {
      void queryClient.invalidateQueries(userAssetsQueryKeys.lists());
    },
    ...options,
  });
}

export function useUserAssetsPagination(params: PaginationOptions) {
  return usePagination<UserAsset>(userAssetsApiUrl, params);
}
